import React from "react";
import { Instagram, Linkedin, Twitter } from "lucide-react";
import styles from "./Footer.module.css";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    const element = document.getElementById(id);
    if (element) {
      e.preventDefault();
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const productLinks = [
    { label: "Auto Apply", href: "/ai-auto-apply/" },
    { label: "Compare LifeShack", href: "/compare/" },
    { label: "Best AI Auto-Apply Tools", href: "/best-ai-auto-apply-tools/" },
    { label: "Resume Builder", href: "/ai-resume/" },
    { label: "Cover Letter Builder", href: "/ai-cover-letter/" },
    { label: "ROI Calculator", href: "/roi-calculator-job-search/" },
    { label: "Our Process", href: "/#process", id: "process" },
    { label: "Pricing", href: "/#plans", id: "plans" },
  ];

  const resourcesLinks = [
    { label: "Jobs by Location", href: "/locations/" },
    { label: "Jobs by Industry", href: "/industries/" },
    { label: "Help Center", href: "/help/" },
    { label: "FAQ", href: "/#faqs", id: "faqs" },
    { label: "Blog", href: "/resources/" },
  ];

  const companyLinks = [
    { label: "About Us", href: "/about/" },
    { label: "Careers", href: "/careers/" },
    { label: "Contact", href: "/contact/" },
  ];

  const legalLinks = [
    { label: "Terms of Service", href: "/terms/" },
    { label: "Privacy Policy", href: "/privacy/" },
    { label: "Cookie Policy", href: "/cookies/" },
  ];

  const openCookieSettings = () => {
    window.dispatchEvent(new Event("lifeshack:open-cookie-settings"));
  };

  const atsPlatforms = [
    { name: "Workday", slug: "workday" },
    { name: "Lever", slug: "lever" },
    { name: "Greenhouse", slug: "greenhouse" },
    { name: "iCIMS", slug: "icims" },
    { name: "BambooHR", slug: "bamboohr" },
    { name: "Jobvite", slug: "jobvite" },
    { name: "AshbyHQ", slug: "ashbyhq" },
    { name: "Workable", slug: "workable" },
    { name: "Breezy", slug: "breezy" },
    { name: "Comeet", slug: "comeet" },
    { name: "Recruitee", slug: "recruitee" },
    { name: "ApplyToJob", slug: "applytojob" },
  ];

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.mainGrid}>
          {/* Brand Column */}
          <div className={styles.brandColumn}>
            <div>
              <div className={styles.logoContainer}>
                <img
                  src="/assets/brand/PrimaryLogoOptimized.svg"
                  alt="LifeShack"
                  width="944"
                  height="281"
                  className={styles.logo}
                />
              </div>
              <p className={styles.tagline}>
                AI-assisted job matching, application materials, supported
                submissions, and activity tracking.
              </p>
            </div>

            {/* Social Links */}
            <div className={styles.socialLinks}>
              <a
                href="https://www.instagram.com/lifeshackofficial/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className={styles.socialLink}
              >
                <Instagram size={18} aria-hidden="true" />
              </a>
              <a
                href="https://www.linkedin.com/company/lifeshack-jobs"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className={styles.socialLink}
              >
                <Linkedin size={18} aria-hidden="true" />
              </a>
              <a
                href="https://x.com/LifeShackAI"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                className={styles.socialLink}
              >
                <Twitter size={18} aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Navigation Columns */}
          <div className={styles.navColumns}>
            {/* Product Column */}
            <div className={styles.navColumn}>
              <h4 className={styles.columnTitle}>Product</h4>
              <ul className={styles.linkList}>
                {productLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className={styles.footerLink}
                      onClick={(e) =>
                        link.id ? handleScroll(e, link.id) : undefined
                      }
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources Column */}
            <div className={styles.navColumn}>
              <h4 className={styles.columnTitle}>Resources</h4>
              <ul className={styles.linkList}>
                {resourcesLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className={styles.footerLink}
                      onClick={(e) =>
                        link.id ? handleScroll(e, link.id) : undefined
                      }
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Integrations Column */}
            <div className={styles.navColumn}>
              <h4 className={styles.columnTitle}>Integrations</h4>
              <ul className={styles.linkList}>
                {atsPlatforms.map((platform) => (
                  <li key={platform.slug}>
                    <a
                      href={`/job-board/${platform.slug}/`}
                      className={styles.footerLink}
                    >
                      {platform.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Column - visible on mobile under Resources */}
            <div className={styles.navColumnLegalMobile}>
              <h4 className={styles.columnTitle}>Legal</h4>
              <ul className={styles.linkList}>
                {legalLinks.map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className={styles.footerLink}>
                      {link.label}
                    </a>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    className={`${styles.footerLink} ${styles.footerButton}`}
                    onClick={openCookieSettings}
                  >
                    Cookie Settings
                  </button>
                </li>
              </ul>
            </div>

            {/* Company & Legal Column - desktop only */}
            <div className={styles.navColumnDouble}>
              <div>
                <h4 className={styles.columnTitle}>Company</h4>
                <ul className={styles.linkList}>
                  {companyLinks.map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className={styles.footerLink}>
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={styles.legalDesktop}>
                <h4 className={styles.columnTitle}>Legal</h4>
                <ul className={styles.linkList}>
                  {legalLinks.map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className={styles.footerLink}>
                        {link.label}
                      </a>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      className={`${styles.footerLink} ${styles.footerButton}`}
                      onClick={openCookieSettings}
                    >
                      Cookie Settings
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={styles.bottomBar}>
          <p className={styles.copyright}>
            © {currentYear} LifeShack. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
